import { useState } from "react";
import { AlertTriangle, Check, ClipboardPaste, X } from "lucide-react";
import { parseEventBrief, type ParsedBrief } from "@/utils/eventBriefParser";

interface EventBriefImporterProps {
  /** Called when the admin accepts the draft. Fills the surrounding form. */
  onApply: (parsed: ParsedBrief) => void;
  /**
   * 'edit' warns that details already entered will be overwritten. The form
   * itself decides what to do about existing questions.
   */
  mode?: "create" | "edit";
}

const SAMPLE = `Udyoga Pramoda Annual Conclave 2026

A day of talks, mentoring and networking.

Date: 27th August 2026, 10:00 AM to 5:00 PM
Venue: JSPN Trust Hall, Bengaluru
Capacity: 200 seats

Registration form:
1. Full name *
2. Email address *
3. Mobile number *
4. T-shirt size (S, M, L, XL)
5. Which sessions will you attend? (select all that apply)
   - Morning keynote
   - Career panel
6. What do you hope to get out of it? (long answer)`;

export default function EventBriefImporter({ onApply, mode = "create" }: EventBriefImporterProps) {
  const isEdit = mode === "edit";
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [parsed, setParsed] = useState<ParsedBrief | null>(null);

  const read = () => setParsed(parseEventBrief(text));

  const apply = () => {
    if (!parsed) return;
    onApply(parsed);
    setOpen(false);
    setParsed(null);
    setText("");
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mb-4 inline-flex items-center gap-2 rounded-md border border-blue-300 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-800 transition-colors hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <ClipboardPaste className="h-4 w-4" />
        {isEdit ? "Paste a brief to update this event" : "Paste an event brief to fill this form"}
      </button>
    );
  }

  return (
    <div className="mb-5 rounded-lg border border-blue-300 bg-blue-50/60 p-4">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Paste an event brief</h3>
          <p className="mt-0.5 text-xs text-gray-600">
            {isEdit
              ? "Paste the announcement text. Whatever it contains replaces what is currently in this form — anything it does not mention is left alone. Nothing is saved until you submit."
              : "Paste the announcement text and its registration questions. Everything below is filled in for you to check and correct — nothing is saved until you submit the form."}
          </p>
        </div>
        <button
          type="button"
          onClick={() => { setOpen(false); setParsed(null); }}
          aria-label="Close"
          className="rounded p-1 text-gray-500 hover:bg-white hover:text-gray-700"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <textarea
        value={text}
        onChange={(e) => { setText(e.target.value); setParsed(null); }}
        rows={10}
        placeholder={SAMPLE}
        className="w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-xs leading-relaxed focus:outline-none focus:ring-2 focus:ring-blue-500"
      />

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={read}
          disabled={!text.trim()}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Read the brief
        </button>
        {!text.trim() && (
          <button
            type="button"
            onClick={() => { setText(SAMPLE); setParsed(null); }}
            className="text-sm font-medium text-blue-700 hover:underline"
          >
            Use the example
          </button>
        )}
      </div>

      {parsed && (
        <div className="mt-4 space-y-3">
          {parsed.found.length > 0 && (
            <div className="rounded-md border border-green-200 bg-green-50 p-3">
              <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-green-800">
                <Check className="h-3.5 w-3.5" /> Found
              </p>
              <ul className="space-y-0.5 text-sm text-green-900">
                {parsed.found.map((f, i) => <li key={i}>{f}</li>)}
              </ul>
            </div>
          )}

          {parsed.warnings.length > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
              <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-amber-800">
                <AlertTriangle className="h-3.5 w-3.5" /> Check these
              </p>
              <ul className="space-y-0.5 text-sm text-amber-900">
                {parsed.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}

          {parsed.fields.length > 0 && (
            <div className="overflow-x-auto rounded-md border border-gray-200 bg-white">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-500">
                    <th className="px-3 py-2 font-semibold">Question</th>
                    <th className="px-3 py-2 font-semibold">Type</th>
                    <th className="px-3 py-2 font-semibold">Required</th>
                    <th className="px-3 py-2 font-semibold">Choices</th>
                  </tr>
                </thead>
                <tbody>
                  {parsed.fields.map((f, i) => (
                    <tr key={i} className="border-b border-gray-100 last:border-0">
                      <td className="px-3 py-2 text-gray-900">{f.fieldLabel}</td>
                      <td className="px-3 py-2"><code className="text-xs text-gray-600">{f.fieldType}</code></td>
                      <td className="px-3 py-2 text-gray-600">{f.required ? "Yes" : "No"}</td>
                      <td className="px-3 py-2 text-gray-600">{f.options?.join(", ") || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3 pt-1">
            <button
              type="button"
              onClick={apply}
              className="rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-green-700"
            >
              Fill the form
              {parsed.fields.length > 0 && ` with ${parsed.fields.length} question${parsed.fields.length === 1 ? "" : "s"}`}
            </button>
            <span className="text-xs text-gray-600">
              {isEdit
                ? "Overwrites the details this brief mentions. You will be asked before existing questions are replaced."
                : "Replaces what is currently in the form. You can edit everything afterwards."}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
